import { isDefinedArray, isDefinedString, reduce } from "et3-type-guards";
import memoize from "lodash/memoize";
import { computed, observable, runInAction } from "mobx";

import { apiCallGetShareholderUpdateData } from "../../api/shareholder-api/api-call-get-shareholder-update";
import { getChartDateMonthYear } from "../../helpers/et3-date-helper";
import getCorePopUpStore from "../core/core-popup-store";
import getCoreUIStore from "../core/core-ui-store";
import { TEt3PickerSingleValue } from "../../TypesNEW";

export type TSharholderUpdateData = {
  id: number;
  real_date: string;
  shown_date: string;
  alkhattaba_total_users: number;
  alkhattaba_new_users: number;
  alkhattaba_total_conversations: number;
  alkhattaba_new_conversations: number;
  alkhattaba_total_messages: number;
  alkhattaba_new_messages: number;
  alkhattaba_sales: number;
  alkhattaba_marketing: number;
};

class ShareholderStore {
  shareholderUpdateData = observable.box<TSharholderUpdateData[]>([]);
  dateRangeMonths = observable.box<TEt3PickerSingleValue>({
    value: "6",
    label: "6 Months"
  });

  shouldShow = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.length !== 0;
  });

  datesToShow = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.map((entry) =>
      getChartDateMonthYear(entry.shown_date)
    );
  });

  totalUsersData = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.map((entry) => entry.alkhattaba_total_users);
  });

  newUsersData = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.map((entry) => entry.alkhattaba_new_users);
  });

  totalConversationData = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.map(
      (entry) => entry.alkhattaba_total_conversations
    );
  });

  newConversationData = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.map(
      (entry) => entry.alkhattaba_new_conversations
    );
  });

  totalMessagesData = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.map(
      (entry) => entry.alkhattaba_total_messages
    );
  });

  newMessagesData = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.map((entry) => entry.alkhattaba_new_messages);
  });

  salesData = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.map((entry) => entry.alkhattaba_sales);
  });

  marketingExpensesData = computed(() => {
    const shareholderUpdateData = this.shareholderUpdateData.get();
    return shareholderUpdateData.map((entry) => entry.alkhattaba_marketing);
  });
  setSharholderUpdateData = (
    shareholderUpdateData: TSharholderUpdateData[]
  ) => {
    runInAction(() => {
      this.shareholderUpdateData.set(shareholderUpdateData);
    });
  };
  setDateRange = (dateRangeMonths: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.dateRangeMonths.set(dateRangeMonths);
    });
  };
  getShareholderUpdateData = async () => {
    getCoreUIStore().setIsLoading("SHARE_HOLDER", true);
    const dateRangeMonthsValue = this.dateRangeMonths.get().value;
    const dateRangeMonths = parseInt(`${dateRangeMonthsValue}`, 10);
    const serverResponse = await apiCallGetShareholderUpdateData({
      dateRangeMonths
    });
    const status = reduce(() => serverResponse.status, "ERROR");
    getCoreUIStore().setIsLoading("SHARE_HOLDER", false);
    if (status === "OK") {
      const data = reduce(() => serverResponse.data, []);
      if (isDefinedArray(data) && !isDefinedString(data)) {
        this.setSharholderUpdateData(data);
        return;
      }
      this.setSharholderUpdateData([]);
    }
    const errorCode = reduce(
      () => serverResponse.code,
      "SERVER_ENCOUNTERED_ERROR"
    );
    getCorePopUpStore().showAlertMessage(errorCode);
  };
}
const getShareholderStore = memoize(
  () => new ShareholderStore(),
  () => 1
);
export default getShareholderStore;
